// 개인정보처리방침 페이지
import React from "react";
import { Link } from "react-router-dom";

const Section: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
  <section className="mb-8">
    <h2 className="mb-3 text-base font-bold text-slate-800">{title}</h2>
    <div className="space-y-2 text-sm leading-relaxed text-slate-600">{children}</div>
  </section>
);

export default function PrivacyPage() {
  return (
    <div className="min-h-screen bg-slate-50">
      {/* 헤더 */}
      <div className="border-b border-slate-200 bg-white">
        <div className="mx-auto max-w-3xl px-4 py-6">
          <Link to="/" className="mb-4 inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-700">
            ← 홈으로
          </Link>
          <h1 className="text-2xl font-extrabold text-slate-900">개인정보처리방침</h1>
          <p className="mt-1 text-sm text-slate-500">시행일: 2025년 6월 1일</p>
        </div>
      </div>

      <div className="mx-auto max-w-3xl px-4 py-10">
        <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm md:p-10">

          <p className="mb-8 text-sm leading-relaxed text-slate-600">
            새로운 연구소(이하 "회사")는 「개인정보 보호법」 제30조에 따라 이용자의 개인정보를 보호하고
            관련 고충을 신속하고 원활하게 처리할 수 있도록 다음과 같이 개인정보처리방침을 수립·공개합니다.
          </p>

          <Section title="제1조 수집하는 개인정보 항목 및 수집 방법">
            <p className="font-semibold text-slate-700">수집 항목</p>
            <ul className="ml-4 list-disc space-y-1">
              <li>필수: 이름, 아이디(ID), 비밀번호, 이메일 주소</li>
              <li>선택: 전화번호</li>
              <li>서비스 이용 시 생성: 학생 정보(이름·학교·학년·성적 데이터), 출석 기록, 상담 기록, 결제 정보</li>
              <li>자동 수집: 서비스 이용 기록, 접속 로그, IP 주소</li>
            </ul>
            <p className="mt-2 font-semibold text-slate-700">수집 방법</p>
            <ul className="ml-4 list-disc space-y-1">
              <li>회원가입 및 서비스 이용 과정에서 이용자가 직접 입력</li>
              <li>서비스 이용 중 자동 생성</li>
            </ul>
          </Section>

          <Section title="제2조 개인정보의 처리 목적">
            <ul className="ml-4 list-disc space-y-1">
              <li>회원 가입·관리: 본인 확인, 서비스 부정 이용 방지</li>
              <li>서비스 제공: 학습 보고서 생성, 출석 관리, 수업료 결제 처리</li>
              <li>학습 분석: AI 기반 시험 분석 및 맞춤 보고서 제공</li>
              <li>민원 처리: 불만 접수 및 처리, 분쟁 조정</li>
            </ul>
          </Section>

          <Section title="제3조 개인정보의 보유 및 이용 기간">
            <ul className="ml-4 list-disc space-y-1">
              <li>회원 정보(아이디·이름·연락처·이메일): 회원 탈퇴 시까지. 탈퇴 요청 시 즉시 서비스 이용이 중단되며, 착오 요청에 대비한 30일의 유예 기간이 지난 후 파기합니다.</li>
              <li>알림 발송용 기기 정보: 탈퇴 요청 즉시 삭제</li>
              <li>원생 학습 기록(학습 보고서·출결·학습 이력): 수강 계약 및 별도 동의에 따라 학원이 보관하며, 탈퇴 후에도 보존됩니다. 삭제를 원하시는 경우 학원으로 요청해 주시기 바랍니다.</li>
              <li>결제·거래 기록: 5년 (「전자상거래 등에서의 소비자 보호에 관한 법률」)</li>
              <li>접속 로그: 3개월 (「통신비밀보호법」)</li>
            </ul>
          </Section>

          <Section title="제4조 개인정보의 제3자 제공">
            <p>회사는 원칙적으로 이용자의 개인정보를 제3자에게 제공하지 않습니다. 다만, 아래의 경우는 예외입니다.</p>
            <ul className="ml-4 mt-2 list-disc space-y-1">
              <li>이용자가 사전에 동의한 경우</li>
              <li>법령에 의거하거나 수사 목적으로 법령에 정해진 절차와 방법에 따라 수사기관의 요구가 있는 경우</li>
            </ul>
          </Section>

          <Section title="제5조 개인정보 처리 위탁">
            <p>회사는 서비스 제공을 위해 아래와 같이 개인정보 처리 업무를 위탁합니다.</p>
            <div className="mt-2 overflow-x-auto rounded-xl border border-slate-200">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-xs text-slate-500">
                  <tr>
                    <th className="px-4 py-2.5 text-left font-semibold">수탁업체</th>
                    <th className="px-4 py-2.5 text-left font-semibold">위탁 업무</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  <tr>
                    <td className="px-4 py-2.5">토스페이먼츠(주)</td>
                    <td className="px-4 py-2.5">결제 처리 및 결제 정보 관리</td>
                  </tr>
                  <tr>
                    <td className="px-4 py-2.5">OpenAI, Inc.</td>
                    <td className="px-4 py-2.5">AI 기반 학습 분석 (익명화된 시험 데이터 전달)</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </Section>

          <Section title="제6조 만 14세 미만 아동의 개인정보 처리">
            <p>회사는 만 14세 미만 아동의 개인정보를 수집·이용하는 경우 「개인정보 보호법」 제22조의2에 따라
              법정대리인의 동의를 받습니다.</p>
            <ul className="ml-4 mt-2 list-disc space-y-1">
              <li>동의 방법: 학원이 <span className="font-semibold">서면 동의서</span>를 통해 법정대리인의 성명을 확인하고
                서명 또는 날인을 받는 방식으로 동의를 받습니다.</li>
              <li>동의 확인 항목: 수집·이용 목적, 수집 항목, 보유 및 이용 기간, 동의 거부 권리와 그에 따른 불이익</li>
              <li>회사는 법정대리인의 동의를 받기 위해 필요한 최소한의 정보(법정대리인의 성명·연락처)만을 수집하며,
                해당 정보를 동의 확인 외의 목적으로 이용하지 않습니다.</li>
              <li>법정대리인은 아동의 개인정보에 대한 열람·정정·삭제·처리정지를 언제든지 요구할 수 있으며,
                요구 시 회사는 지체 없이 필요한 조치를 취합니다.</li>
              <li>법정대리인이 동의를 철회하는 경우 해당 아동의 개인정보 처리를 중단하고 제8조에 따라 파기합니다.</li>
            </ul>
            <p className="mt-2">동의서 원본은 학원이 보관하며, 수강 계약 종료 후 보존 의무 기간이 지나면 파기합니다.</p>
          </Section>

          <Section title="제7조 정보주체의 권리·의무 및 행사 방법">
            <p>이용자는 언제든지 아래 권리를 행사할 수 있습니다.</p>
            <ul className="ml-4 mt-2 list-disc space-y-1">
              <li>개인정보 열람 요구</li>
              <li>개인정보 정정·삭제 요구</li>
              <li>개인정보 처리 정지 요구</li>
            </ul>
            <p className="mt-2">권리 행사는 마이페이지 또는 아래 개인정보보호책임자 이메일로 요청하실 수 있으며,
              회사는 요청 접수 후 10일 이내 조치합니다.</p>
          </Section>

          <Section title="제8조 개인정보의 파기">
            <p>회사는 개인정보 보유 기간이 경과하거나 처리 목적이 달성된 경우 지체 없이 해당 개인정보를 파기합니다.</p>
            <ul className="ml-4 mt-2 list-disc space-y-1">
              <li>전자적 파일 형태: 복구 불가능한 방법으로 영구 삭제</li>
              <li>종이 문서: 분쇄 또는 소각</li>
            </ul>
          </Section>

          <Section title="제9조 개인정보 자동 수집 장치의 설치·운영 및 거부">
            <p>회사는 이용자에게 개별화된 서비스를 제공하기 위해 이용 정보를 저장하고 수시로 불러오는
              기술(세션 스토리지·로컬 스토리지)을 사용합니다. 이용자는 웹 브라우저 설정을 통해 이를
              거부할 수 있으나, 일부 서비스 이용이 제한될 수 있습니다.</p>
          </Section>

          <Section title="제10조 개인정보보호책임자">
            <div className="rounded-xl border border-slate-200 bg-slate-50 p-4">
              <p className="font-semibold text-slate-700">개인정보보호책임자</p>
              <ul className="mt-2 space-y-1">
                <li>성명: 김준영</li>
                <li>이메일: sluygs1000@gmail.com</li>
                <li>연락처: 02-2061-7942</li>
              </ul>
              <p className="mt-3 text-xs text-slate-500">
                개인정보 관련 민원은 개인정보보호위원회(privacy.go.kr) 또는
                개인정보침해 신고센터(118)에 신고하실 수 있습니다.
              </p>
            </div>
          </Section>

          <Section title="제11조 개인정보처리방침의 변경">
            <p>이 방침은 2025년 6월 1일부터 적용되었으며, 만 14세 미만 아동의 개인정보 처리에 관한 사항(제6조)을
              추가하여 아래와 같이 개정되었습니다. 내용이 변경될 경우 서비스 내 공지사항을 통해
              변경 내용과 시행일을 안내합니다.</p>
            <ul className="ml-4 mt-2 list-disc space-y-1">
              <li>공고일자: 2026년 9월 14일</li>
              <li>시행일자: 2026년 9월 21일</li>
            </ul>
          </Section>

        </div>
      </div>
    </div>
  );
}
